import {
  HttpClient,
  HttpEvent,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiEndpoints } from '../common/config';
import { environment } from '../../../environments/environment';
import { UploadFileModel } from '../interfaces/routing/blueprintMangement-data.interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  constructor(private readonly http: HttpClient) {}

  getConfiguration(propertyName: string): Observable<string> {
    return this.http
      .get<any>(`${ApiEndpoints.configurations.GetByName}/${propertyName}`)
      .pipe(map((res: any) => res.Data.PropertyValue));
  }

  /**
   * get grid Data
   */
  getServiceAreas(): Observable<any> {
    return this.http.post<any>(ApiEndpoints.shared.filters.serviceAreas, {
      searchTerm: '',
      state: '',
      operatorId: 0,
    });
  }

  uploadFile(uploadFileModel: any): Observable<HttpEvent<any>> {
    const req = new HttpRequest(
      'POST',
      ApiEndpoints.shared.files.uploadFile,
      uploadFileModel,
      {
        reportProgress: true,
      },
    );

    return this.http.request(req);
  }

  downloadFile(uploadFileModel: UploadFileModel) {
    return this.http.post<any>(
      `${ApiEndpoints.shared.files.downloadFile}`,
      uploadFileModel,
    );
  }

  deleteFile(
    fileId: string,
    driverId: string,
    typeName: string,
    vehicleId?: string,
  ): Observable<any> {
    return this.http.delete<any>(
      `${ApiEndpoints.shared.files.deleteFile}/${fileId}?partnerId=${driverId}&typeName=${typeName} `,
    );
  }

  /**
   * get grid Data
   */
  getStates(): Observable<any> {
    return this.http.get<any>(ApiEndpoints.shared.filters.states);
  }

  /**
   * get payouts periods-last-year
   */
  getPayoutsLastYear(): Observable<any> {
    return this.http.get<any>(
      ApiEndpoints.shared.filters.PayoutsPeriodsLastYear,
    );
  }

  /**
   * get drivers names
   */
  getDriversNames(params: any): Observable<any> {
    return this.http.post<any>(ApiEndpoints.drivers.driversAndMonitors, params);
  }

  /**
   * Get contractors by service provider ID.
   * Returns a normalized { label, value }[] regardless of the underlying API.
   * @param serviceProvider - The provider ID
   * @param isForRides - true  → arrival-time-report/drivers (RidesPartnersModel: FirstName, LastName)
   *                     false → provider-partners            (PartnersModel: PartnerFirstName, PartnerLastName)
   */
  getContractors(
    serviceProvider: string,
    isForRides: boolean = false,
    withLeads: boolean = false,
  ): Observable<{ label: string; value: string }[]> {
    let params = new HttpParams().set('serviceProvider', serviceProvider);
    if (!isForRides) {
      params = params.set('withLeads', withLeads);
    }
    const endpoint = isForRides
      ? ApiEndpoints.rides.contractors
      : ApiEndpoints.shared.filters.providerPartners;
    return this.http.get<any>(endpoint, { params }).pipe(
      map(res =>
        (res.Data ?? []).map((c: any) => ({
          label: isForRides
            ? `${c.FirstName} ${c.LastName}`
            : `${c.PartnerFirstName} ${c.PartnerLastName}`,
          value: c.PartnerId.toString(),
        })),
      ),
    );
  }

  /**
   * Get contractor data by ID
   * @param id - The contractor ID to fetch data for
   * @returns Observable<any>
   */
  getContractorData(id: string): Observable<any> {
    const endpoint = `${ApiEndpoints.drivers.popups.contractors}/${id}`;
    return this.http.get<any>(endpoint);
  }
  /**
   * get Period
   */
  getPeriod(year: number): Observable<any> {
    return this.http.get<any>(
      `${ApiEndpoints.accounting.periodFilters}?year=${year}`,
    );
  }
  /**
   * get Partners For Accounting
   */
  getPartnersForAccounting(params: any): Observable<any> {
    const httpParams = new HttpParams({ fromObject: params });
    const url = ApiEndpoints.accounting.PartnersForAccounting;
    return this.http.get<any>(url, { params: httpParams });
  }

  /**
   * get drivers names for SubDriver modal
   */
  getDriversNamesSub(params: any): Observable<any> {
    return this.http.post<any>(ApiEndpoints.shared.filters.subDrivers, params);
  }

  /**
   * get Period
   */
  getBluePrintSubDriver(Uuid: string): Observable<any> {
    return this.http.get<any>(
      `${ApiEndpoints.shared.filters.bluePrintSubDriver}/${Uuid}/driver`,
    );
  }

  /**
   * get drivers names
   */
  updateDrivers(params: any): Observable<any> {
    return this.http.patch<any>(
      `${ApiEndpoints.shared.filters.bluePrintSubDriverUpdate}/${params.rideUuid}/update-driver`,
      params,
    );
  }
  /**
   * get Places Data
   */
  getPlaces(providerID: number): Observable<any> {
    let params = new HttpParams();

    params = params.set('serviceProvider', providerID);

    const endpoint = ApiEndpoints.shared.filters.places;
    return this.http.get<any>(endpoint, { params });
  }

  /**
   * change password
   */
  changePassword(params: any): Observable<any> {
    return this.http.patch<any>(
      ApiEndpoints.shared.account.changePassword,
      params,
    );
  }

  /**
   * logout password
   */
  logout(): Observable<any> {
    return this.http.patch<any>(ApiEndpoints.shared.account.logOut, '');
  }

  getGoogleAPIKey() {
    let propertyName = 'GoogleAPIKeyProvider';
    return this.http.get<any>(
      `${ApiEndpoints.configurations.GetByName}/${propertyName}`,
    );
  }

  /**
   * Get RSA Public Key for encryption operations
   * @returns Observable with RSA public key configuration
   */
  getRSAPublicKey(): Observable<any> {
    const propertyName = 'RSAPublicKey';
    return this.http.get<any>(
      `${ApiEndpoints.configurations.GetByName}/${propertyName}`,
    );
  }

  refreshToken() {
    return this.http.get<any>(`${ApiEndpoints.shared.account.refreshToken}`);
  }
  createLead(leadModel: any) {
    return this.http.post<any>(
      `${environment.apiUrl}/provider/partners/leads/add`,
      leadModel,
    );
  }

  /**
   * Download and view file by converting base64 to Blob and opening in new tab
   * @param uploadFileModel - File model containing file details
   */
  downloadAndViewFile(uploadFileModel: UploadFileModel): void {
    this.downloadFile(uploadFileModel).subscribe({
      next: response => {
        if (response?.Data.Value) {
          this.openBase64InNewTab(
            response.Data.Value,
            this.getFileTypeForMime(response.Data.Extension),
          );
        }
      },
      error: error => {
        console.error('Error downloading file:', error);
      },
    });
  }

  /**
   * Open base64 file in new tab by converting to Blob
   * @param base64String - Base64 encoded file data
   * @param fileType - MIME type of the file
   */
  private openBase64InNewTab(base64String: string, fileType: string): void {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const file = new Blob([byteArray], { type: fileType });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, '_blank');
  }

  /**
   * Get MIME type for file extension
   * @param extension - File extension
   * @returns MIME type string
   */
  private getFileTypeForMime(extension: string): string {
    switch (extension?.toLowerCase()) {
      case 'pdf':
        return 'application/pdf';
      case 'png':
        return 'image/png';
      case 'jpeg':
      case 'jpg':
        return 'image/jpg';
      case 'doc':
      case 'docx':
        return 'application/msword';
      default:
        return 'application/octet-stream';
    }
  }
  getVersion(projectName: string): Observable<string> {
    return this.http.get(`${ApiEndpoints.about.getVersionSettings}/${projectName}`, { responseType: 'text' })
  }

}
